import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

export interface FileValidationConfig {
  maxBytes: number;
  allowedExtensions: string[];
}

export interface UploadStepFileProps {
  /** Currently selected file (controlled by the parent one-step dialog). */
  initialFile?: File | null;
  config: FileValidationConfig;
  /** Invoked with a validated file. The parent decides what to do with it. */
  onFileAccepted: (file: File) => void;
  /** Disables the picker while a hash/upload is in flight. */
  disabled?: boolean;
}

function extensionOf(name: string): string {
  const idx = name.lastIndexOf(".");
  if (idx === -1) return "";
  return name.slice(idx + 1).toLowerCase();
}

function formatMb(bytes: number): number {
  return Math.round(bytes / (1024 * 1024));
}

/**
 * Compact file zone for the single-screen upload dialog. Before selection it
 * renders a drag-drop / click target; once a valid file is chosen it collapses
 * to a name + size line with a «Заменить» action. Validation errors (size /
 * extension) render inline and leave the previous selection untouched.
 */
export function UploadStepFile({
  initialFile = null,
  config,
  onFileAccepted,
  disabled = false,
}: UploadStepFileProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<File | null>(initialFile);
  const inputRef = useRef<HTMLInputElement | null>(null);

  function validate(file: File): string | null {
    if (file.size > config.maxBytes) {
      return t("meetings.upload.fileTooLarge", {
        maxMb: formatMb(config.maxBytes),
      });
    }
    const ext = extensionOf(file.name);
    if (!config.allowedExtensions.includes(ext)) {
      return t("meetings.upload.extensionNotAllowed", {
        allowed: config.allowedExtensions.join(", "),
      });
    }
    return null;
  }

  function accept(file: File): void {
    const err = validate(file);
    if (err !== null) {
      setError(err);
      return;
    }
    setError(null);
    setSelected(file);
    onFileAccepted(file);
  }

  function openPicker(): void {
    if (!disabled) inputRef.current?.click();
  }

  function onInputChange(evt: ChangeEvent<HTMLInputElement>): void {
    const file = evt.target.files?.[0] ?? null;
    if (file !== null) accept(file);
  }

  function onDrop(evt: DragEvent<HTMLDivElement>): void {
    evt.preventDefault();
    if (disabled) return;
    const file = evt.dataTransfer.files?.[0] ?? null;
    if (file !== null) accept(file);
  }

  function onDragOver(evt: DragEvent<HTMLDivElement>): void {
    evt.preventDefault();
  }

  return (
    <div className="space-y-2">
      {selected === null ? (
        <div
          role="button"
          tabIndex={0}
          aria-disabled={disabled}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onClick={openPicker}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") openPicker();
          }}
          className="flex h-24 cursor-pointer items-center justify-center rounded-md border border-dashed border-muted-foreground/40 px-3 text-center text-sm text-muted-foreground hover:bg-accent/30 aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
          data-testid="upload-drop-zone"
        >
          {t("meetings.upload.dropZone")}
        </div>
      ) : (
        <div
          className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2 text-sm"
          data-testid="upload-selected-file"
        >
          <span className="truncate">
            {selected.name} ({formatMb(selected.size)} МБ)
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={openPicker}
          >
            {t("meetings.upload.replace")}
          </Button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={config.allowedExtensions.map((e) => `.${e}`).join(",")}
        onChange={onInputChange}
        data-testid="upload-file-input"
      />
      {error !== null ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
